import type { ProductData, TooltipThemeVars } from "../types";

const TOOLTIP_STYLE_ID = "pt-tooltip-style";

const BASE_CSS = `
.pt-match {
  color: var(--pt-accent, #005fcc);
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  cursor: pointer;
  transition: color 120ms ease;
}

.pt-match:focus-visible {
  outline: 2px solid var(--pt-accent, #005fcc);
  outline-offset: 2px;
}

.pt-match--disabled {
  cursor: default;
  text-decoration: none;
  color: inherit;
}

.pt-tooltip {
  position: fixed;
  z-index: 2147483000;
  width: min(500px, calc(100vw - 20px));
  color: var(--pt-color, #1f2937);
  overflow: visible;
  font-family: "IBM Plex Sans", "Segoe UI", sans-serif;
  --pt-arrow-left: 32px;
}

.pt-tooltip[hidden] {
  display: none;
}

.pt-tooltip::before,
.pt-tooltip::after {
  content: "";
  position: absolute;
  left: var(--pt-arrow-left);
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-style: solid;
}

.pt-tooltip[data-placement="bottom"]::before {
  top: -10px;
  border-width: 0 10px 10px 10px;
  border-color: transparent transparent var(--pt-border, #d1d5db) transparent;
}

.pt-tooltip[data-placement="bottom"]::after {
  top: -8px;
  border-width: 0 9px 9px 9px;
  border-color: transparent transparent var(--pt-bg, #ffffff) transparent;
}

.pt-tooltip[data-placement="top"]::before {
  bottom: -10px;
  border-width: 10px 10px 0 10px;
  border-color: var(--pt-border, #d1d5db) transparent transparent transparent;
}

.pt-tooltip[data-placement="top"]::after {
  bottom: -8px;
  border-width: 9px 9px 0 9px;
  border-color: var(--pt-bg, #ffffff) transparent transparent transparent;
}

.pt-tooltip--loading {
  cursor: progress;
}

.pt-tooltip--cta-only .pt-tooltip__card {
  min-width: min(320px, calc(100vw - 20px));
}

.pt-tooltip--cta-only .pt-tooltip__actions {
  padding: 18px;
}

.pt-tooltip__card {
  display: flex;
  flex-direction: column;
  background: var(--pt-bg, #ffffff);
  border: 1px solid var(--pt-border, #d1d5db);
  border-radius: var(--pt-radius, 14px);
  box-shadow: var(--pt-shadow, 0 14px 36px rgba(0, 0, 0, 0.18));
  overflow: hidden;
}

.pt-tooltip__main {
  display: flex;
  align-items: stretch;
  min-height: 96px;
}

.pt-tooltip__media {
  position: relative;
  width: 96px;
  min-height: 96px;
  flex: 0 0 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border-right: 1px solid rgba(148, 163, 184, 0.2);
}

.pt-tooltip__image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 10px;
}

.pt-tooltip__image-placeholder {
  width: 68px;
  height: 68px;
  border-radius: 10px;
  border: 1px dashed #cbd5e1;
  background: #f1f5f9;
}

.pt-tooltip__body {
  flex: 1;
  min-width: 0;
  padding: 12px 12px 10px;
}

.pt-tooltip__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 16px;
  margin-bottom: 6px;
}

.pt-tooltip__discount {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  background: #fedb54;
  color: #3f3f46;
  border: 1px solid #f9c248;
}

.pt-tooltip__vendor {
  font-size: 11px;
  line-height: 1.2;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pt-tooltip__title {
  margin: 0;
  font-size: 14px;
  line-height: 1.32;
  font-weight: 700;
  color: #0f172a;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pt-tooltip__price-row {
  margin: 8px 0 0;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.pt-tooltip__price-current {
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: #020617;
}

.pt-tooltip__price-original {
  color: #94a3b8;
  text-decoration: line-through;
}

.pt-tooltip__description {
  margin: 7px 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: #475569;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pt-tooltip__actions {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 12px 12px 0;
  flex: 0 0 auto;
}

.pt-tooltip__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 88px;
  height: 38px;
  padding: 0 14px;
  border-radius: 10px;
  border: 1px solid #f9c248;
  text-decoration: none;
  background: #fedb54;
  color: #27272a;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.35) inset;
  transition:
    background-color 140ms ease,
    border-color 140ms ease,
    transform 140ms ease,
    box-shadow 140ms ease;
}

.pt-tooltip__cta:hover {
  background: #facc15;
  border-color: #eab308;
  transform: translateY(-1px);
  box-shadow:
    0 8px 16px rgba(234, 179, 8, 0.24),
    0 1px 0 rgba(255, 255, 255, 0.35) inset;
}

.pt-tooltip__cta:focus-visible {
  outline: 2px solid #ca8a04;
  outline-offset: 2px;
  background: #facc15;
  border-color: #eab308;
}

.pt-tooltip__footer {
  padding: 9px 12px 11px;
  border-top: 1px solid rgba(148, 163, 184, 0.16);
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.75), rgba(255, 255, 255, 0.98));
}

.pt-tooltip__credit {
  margin: 0;
  font-size: 11px;
  line-height: 1.35;
  color: #94a3b8;
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.pt-tooltip__credit-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  filter: grayscale(1) saturate(0.15);
  opacity: 0.72;
}

.pt-tooltip__credit-copy {
  min-width: 0;
}

.pt-tooltip__credit-tail {
  white-space: nowrap;
}

.pt-tooltip__credit-link {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.pt-tooltip__credit-link:hover,
.pt-tooltip__credit-link:focus-visible {
  color: inherit;
}

.pt-skeleton {
  background: linear-gradient(90deg, #e2e8f0 0%, #f8fafc 45%, #e2e8f0 100%);
  background-size: 220% 100%;
  animation: pt-shimmer 1.2s linear infinite;
  border-radius: 8px;
}

.pt-tooltip__skeleton-media {
  width: 72px;
  height: 72px;
}

.pt-tooltip__skeleton-discount {
  width: 40px;
  height: 14px;
  border-radius: 999px;
}

.pt-tooltip__skeleton-vendor {
  width: 52px;
  height: 10px;
}

.pt-tooltip__skeleton-title-1 {
  width: 95%;
  height: 11px;
  margin-top: 2px;
}

.pt-tooltip__skeleton-title-2 {
  width: 68%;
  height: 11px;
  margin-top: 6px;
}

.pt-tooltip__skeleton-price {
  width: 88px;
  height: 11px;
  margin-top: 8px;
}

.pt-tooltip__skeleton-price-old {
  width: 56px;
  height: 10px;
  margin-top: 8px;
}

.pt-tooltip__skeleton-cta {
  width: 88px;
  height: 38px;
  border-radius: 10px;
}

.pt-tooltip__skeleton-credit {
  width: 68%;
  height: 10px;
}

@keyframes pt-shimmer {
  0% {
    background-position: 220% 0;
  }
  100% {
    background-position: -120% 0;
  }
}
`;

export class TooltipView {
  readonly element: HTMLDivElement;

  constructor(theme: Partial<TooltipThemeVars> | undefined, openInNewTab: boolean) {
    ensureBaseStyles();
    this.element = document.createElement("div");
    this.element.className = "pt-tooltip";
    this.element.hidden = true;
    this.element.setAttribute("role", "dialog");
    this.element.setAttribute("aria-live", "polite");
    this.element.dataset.openInNewTab = openInNewTab ? "1" : "0";

    applyTheme(this.element, theme);
    document.body.appendChild(this.element);
  }

  show(anchor: HTMLElement, product: ProductData, openInNewTab: boolean): void {
    this.element.classList.remove("pt-tooltip--loading");
    this.element.setAttribute("aria-busy", "false");
    this.renderProduct(product, openInNewTab);
    this.element.hidden = false;
    this.position(anchor);
  }

  showLoading(anchor: HTMLElement): void {
    this.renderLoading();
    this.element.classList.add("pt-tooltip--loading");
    this.element.setAttribute("aria-busy", "true");
    this.element.hidden = false;
    this.position(anchor);
  }

  hide(): void {
    this.element.classList.remove("pt-tooltip--loading");
    this.element.classList.remove("pt-tooltip--cta-only");
    this.element.setAttribute("aria-busy", "false");
    this.element.hidden = true;
  }

  contains(node: Node | null): boolean {
    if (!node) {
      return false;
    }
    return this.element.contains(node);
  }

  destroy(): void {
    this.element.remove();
  }

  private renderProduct(product: ProductData, openInNewTab: boolean): void {
    this.element.innerHTML = "";
    this.element.classList.toggle("pt-tooltip--cta-only", product.displayMode === "cta-only");
    if (product.displayMode === "cta-only") {
      this.renderCtaOnlyProduct(product, openInNewTab);
      return;
    }

    this.renderFullProduct(product, openInNewTab);
  }

  private renderFullProduct(product: ProductData, openInNewTab: boolean): void {
    const card = document.createElement("div");
    card.className = "pt-tooltip__card";
    const main = document.createElement("div");
    main.className = "pt-tooltip__main";

    const media = document.createElement("div");
    media.className = "pt-tooltip__media";
    if (product.imageUrl) {
      const image = document.createElement("img");
      image.className = "pt-tooltip__image";
      image.src = product.imageUrl;
      image.alt = product.title;
      image.loading = "lazy";
      image.width = 96;
      image.height = 96;
      media.appendChild(image);
    } else {
      const placeholder = document.createElement("div");
      placeholder.className = "pt-tooltip__image-placeholder";
      media.appendChild(placeholder);
    }
    main.appendChild(media);

    const body = document.createElement("div");
    body.className = "pt-tooltip__body";

    const meta = document.createElement("div");
    meta.className = "pt-tooltip__meta";
    if (product.discountLabel) {
      const discount = document.createElement("span");
      discount.className = "pt-tooltip__discount";
      discount.textContent = product.discountLabel;
      meta.appendChild(discount);
    }

    const vendor = document.createElement("span");
    vendor.className = "pt-tooltip__vendor";
    vendor.textContent = product.vendor ?? inferVendorFromUrl(product.url);
    meta.appendChild(vendor);
    body.appendChild(meta);

    const title = document.createElement("h3");
    title.className = "pt-tooltip__title";
    title.textContent = product.title;
    body.appendChild(title);

    if (product.price) {
      const priceRow = document.createElement("p");
      priceRow.className = "pt-tooltip__price-row";

      const currentPrice = document.createElement("span");
      currentPrice.className = "pt-tooltip__price-current";
      currentPrice.textContent = product.price;
      priceRow.appendChild(currentPrice);

      if (product.originalPrice) {
        const originalPrice = document.createElement("span");
        originalPrice.className = "pt-tooltip__price-original";
        originalPrice.textContent = product.originalPrice;
        priceRow.appendChild(originalPrice);
      }

      body.appendChild(priceRow);
    }

    if (product.description) {
      const description = document.createElement("p");
      description.className = "pt-tooltip__description";
      description.textContent = product.description;
      body.appendChild(description);
    }

    main.appendChild(body);

    const actions = document.createElement("div");
    actions.className = "pt-tooltip__actions";
    const cta = document.createElement("a");
    cta.className = "pt-tooltip__cta";
    cta.href = product.url;
    cta.textContent = product.ctaLabel ?? "Acquista";
    cta.dataset.role = "tooltip-link";
    cta.setAttribute("aria-label", `${cta.textContent} ${product.title}`);
    if (openInNewTab) {
      cta.target = "_blank";
      cta.rel = "noopener noreferrer";
    }
    actions.appendChild(cta);
    main.appendChild(actions);
    card.appendChild(main);

    const footer = document.createElement("div");
    footer.className = "pt-tooltip__footer";
    const credit = document.createElement("p");
    credit.className = "pt-tooltip__credit";
    const creditIcon = document.createElement("span");
    creditIcon.className = "pt-tooltip__credit-icon";
    creditIcon.setAttribute("aria-hidden", "true");
    creditIcon.textContent = "✨";
    credit.appendChild(creditIcon);

    const creditCopy = document.createElement("span");
    creditCopy.className = "pt-tooltip__credit-copy";
    creditCopy.append("Offerta scoperta grazie all'Intelligenza Artificiale di ");

    const creditLink = document.createElement("a");
    creditLink.className = "pt-tooltip__credit-link";
    creditLink.href = "https://3labs.it";
    creditLink.target = "_blank";
    creditLink.rel = "noopener noreferrer";
    creditLink.textContent = "3Labs";
    const creditTail = document.createElement("span");
    creditTail.className = "pt-tooltip__credit-tail";
    creditTail.appendChild(creditLink);
    creditTail.append(".");
    creditCopy.appendChild(creditTail);
    credit.appendChild(creditCopy);
    footer.appendChild(credit);
    card.appendChild(footer);
    this.element.appendChild(card);
  }

  private renderCtaOnlyProduct(product: ProductData, openInNewTab: boolean): void {
    const card = document.createElement("div");
    card.className = "pt-tooltip__card";

    const actions = document.createElement("div");
    actions.className = "pt-tooltip__actions";
    const cta = document.createElement("a");
    cta.className = "pt-tooltip__cta";
    cta.href = product.url;
    cta.textContent = product.ctaLabel ?? "Vai al prodotto";
    cta.dataset.role = "tooltip-link";
    cta.setAttribute("aria-label", `${cta.textContent} ${product.title}`);
    if (openInNewTab) {
      cta.target = "_blank";
      cta.rel = "noopener noreferrer";
    }

    actions.appendChild(cta);
    card.appendChild(actions);
    this.element.appendChild(card);
  }

  private renderLoading(): void {
    this.element.innerHTML = `
      <div class="pt-tooltip__card" aria-hidden="true">
        <div class="pt-tooltip__main">
          <div class="pt-tooltip__media">
            <div class="pt-skeleton pt-tooltip__skeleton-media"></div>
          </div>
          <div class="pt-tooltip__body">
            <div class="pt-tooltip__meta">
              <div class="pt-skeleton pt-tooltip__skeleton-discount"></div>
              <div class="pt-skeleton pt-tooltip__skeleton-vendor"></div>
            </div>
            <div class="pt-skeleton pt-tooltip__skeleton-title-1"></div>
            <div class="pt-skeleton pt-tooltip__skeleton-title-2"></div>
            <div class="pt-tooltip__price-row">
              <div class="pt-skeleton pt-tooltip__skeleton-price"></div>
              <div class="pt-skeleton pt-tooltip__skeleton-price-old"></div>
            </div>
          </div>
          <div class="pt-tooltip__actions">
            <div class="pt-skeleton pt-tooltip__skeleton-cta"></div>
          </div>
        </div>
        <div class="pt-tooltip__footer">
          <div class="pt-skeleton pt-tooltip__skeleton-credit"></div>
        </div>
      </div>
    `;
  }

  private position(anchor: HTMLElement): void {
    const spacing = 10;
    const arrowWidth = 20;
    const arrowInset = 18;
    const anchorRect = anchor.getBoundingClientRect();

    this.element.style.left = "0px";
    this.element.style.top = "0px";
    this.element.style.visibility = "hidden";

    const tooltipRect = this.element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = anchorRect.bottom + spacing;
    let placement: "top" | "bottom" = "bottom";
    if (top + tooltipRect.height > viewportHeight - spacing) {
      top = Math.max(spacing, anchorRect.top - tooltipRect.height - spacing);
      placement = "top";
    }

    let left = anchorRect.left;
    if (left + tooltipRect.width > viewportWidth - spacing) {
      left = viewportWidth - tooltipRect.width - spacing;
    }
    left = Math.max(spacing, left);

    this.element.style.left = `${Math.round(left)}px`;
    this.element.style.top = `${Math.round(top)}px`;
    this.element.dataset.placement = placement;
    const anchorCenter = anchorRect.left + anchorRect.width / 2;
    const arrowLeft = clamp(anchorCenter - left, arrowInset, tooltipRect.width - arrowInset - arrowWidth / 2);
    this.element.style.setProperty("--pt-arrow-left", `${Math.round(arrowLeft)}px`);
    this.element.style.visibility = "visible";
  }
}

function ensureBaseStyles(): void {
  if (document.getElementById(TOOLTIP_STYLE_ID)) {
    return;
  }

  const style = document.createElement("style");
  style.id = TOOLTIP_STYLE_ID;
  style.textContent = BASE_CSS;
  document.head.appendChild(style);
}

function applyTheme(element: HTMLElement, theme: Partial<TooltipThemeVars> | undefined): void {
  if (!theme) {
    return;
  }

  if (theme.background) {
    element.style.setProperty("--pt-bg", theme.background);
  }
  if (theme.color) {
    element.style.setProperty("--pt-color", theme.color);
  }
  if (theme.border) {
    element.style.setProperty("--pt-border", theme.border);
  }
  if (theme.shadow) {
    element.style.setProperty("--pt-shadow", theme.shadow);
  }
  if (theme.accent) {
    element.style.setProperty("--pt-accent", theme.accent);
  }
  if (theme.radius) {
    element.style.setProperty("--pt-radius", theme.radius);
  }
}

function inferVendorFromUrl(url: string): string {
  try {
    const hostname = new URL(url).hostname.toLocaleLowerCase();
    if (hostname.includes("amazon")) {
      return "Amazon";
    }

    const chunks = hostname.replace(/^www\./, "").split(".");
    if (chunks.length > 0 && chunks[0]) {
      return chunks[0].charAt(0).toUpperCase() + chunks[0].slice(1);
    }
  } catch {
    // Ignore malformed URLs and fallback to generic vendor.
  }

  return "Shop";
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
